import React, { useState, useEffect, useRef } from 'react';
import {
    Modal,
    Group,
    Heading,
    List,
    IconButton,
    Badge,
    Box,
    CloseOutlined,
    Button,
    useBreakpoint,
    Scrollable,
} from '@landr/maestro';
import { cva } from '@landr/styled-system/css';
import { Trans } from '@lingui/macro';
import { FiltersParams, TagCategoryParams } from 'types/params';
import useAdvancedFilter from './hooks/useAdvancedFilter';
import Filters from './FiltersContent/AdvancedFiltersContent';
import TagsFiltersContent from './FiltersContent/AdvancedTagsContent';

interface AdvancedFiltersModalContentProps {
    handleClose: () => void;
}

export const footerHeight = 80;
export const sideNavigationWidth = 280;

const FilterBadge: React.FC<{
    filter: FiltersParams | TagCategoryParams;
}> = ({ filter }) => {
    const { getFilterState, getTagsCategoryState, getIsTagsCategory } =
        useAdvancedFilter();

    const isTagsCategory = getIsTagsCategory(filter);
    const showBadge = isTagsCategory
        ? (getTagsCategoryState(filter)?.length ?? 0) > 0
        : (getFilterState(filter)?.length ?? 0) > 0;

    return (
        showBadge && (
            <Group.Item alignItems={'center'} display={'flex'}>
                <Badge
                    label={
                        isTagsCategory
                            ? getTagsCategoryState(filter)?.length
                            : getFilterState(filter)?.length
                    }
                    variant="inverted"
                    isRounded
                    size="sm"
                />
            </Group.Item>
        )
    );
};

const AdvancedFiltersModalData: React.FC<{
    name: string;
    handleClose: () => void;
    selectedFilter: FiltersParams | TagCategoryParams;
}> = ({ name, handleClose, selectedFilter }) => {
    const { getTagsCategoryState, getIsTagsCategory, getFilterResults } =
        useAdvancedFilter();
    const contentRef = useRef<HTMLDivElement>(null);
    const isTablet = useBreakpoint('lg');

    useEffect(() => {
        if (contentRef.current) {
            contentRef.current.scrollTop = 0;
        }
    }, [selectedFilter]);

    const isTagsCategory = getIsTagsCategory(selectedFilter);

    return (
        <Group.Item
            width={`calc(100% - ${sideNavigationWidth}px)`}
            height="100%"
            position="relative"
            overflow={'hidden'}
        >
            <Group
                direction="horizontal"
                justifyContent="space-between"
                width="100%"
                padding="md"
                overflow={'hidden'}
            >
                <Group.Item>
                    <Heading fontSize="xl" color="text.high" marginTop="2px">
                        {name}
                    </Heading>
                </Group.Item>
                <Group.Item>
                    <IconButton
                        icon={CloseOutlined}
                        variant="ghost"
                        size="sm"
                        onClick={handleClose}
                        isRounded
                        label="Close"
                        padding="0"
                    />
                </Group.Item>
            </Group>
            <Group
                height={`calc(100% - ${footerHeight}px)`}
                position="relative"
                paddingTop="lg"
                paddingLeft={isTablet ? 'sm' : '2xl'}
                overflow={'hidden'}
            >
                <Scrollable
                    maxHeight="100%"
                    width="100%!important"
                    ref={contentRef}
                >
                    <Group.Item
                        height={'100%'}
                        position="relative"
                        width="100%"
                        marginBottom={'200px'}
                        maxWidth="800px"
                    >
                        {selectedFilter === FiltersParams.instruments && (
                            <Filters filterType={FiltersParams.instruments} />
                        )}
                        {selectedFilter === FiltersParams.genres && (
                            <Filters filterType={FiltersParams.genres} />
                        )}
                        {selectedFilter === FiltersParams.sfxs && (
                            <Filters filterType={FiltersParams.sfxs} />
                        )}
                        {isTagsCategory && (
                            <TagsFiltersContent
                                filterType={selectedFilter}
                                tags={getFilterResults(selectedFilter)}
                                activeTags={getTagsCategoryState(
                                    selectedFilter,
                                )}
                                tagsCategory={selectedFilter}
                            />
                        )}
                    </Group.Item>
                </Scrollable>
            </Group>
        </Group.Item>
    );
};

export const listItemStyles = cva({
    base: {
        _hover: {
            backgroundColor: 'surface.neutral.medium.hover',
        },
        color: 'text.medium',
    },
    variants: {
        isSelected: {
            true: {
                backgroundColor: 'surface.neutral.medium.hover',
                color: 'text.high',
            },
        },
    },
});

export const AdvancedFiltersModalContent: React.FC<
    AdvancedFiltersModalContentProps
> = ({ handleClose }) => {
    const {
        handleClearCta,
        resultsCopy,
        getFilterIcon,
        getFilterName,
        getFilterResults,
        filtersAppliedTotal,
        availableAdvancedFilters,
    } = useAdvancedFilter();

    const [selectedFilter, setSelectedFilter] = useState<
        FiltersParams | TagCategoryParams
    >(FiltersParams.instruments);

    return (
        <Modal.Content position="relative" height="100%" overflow={'hidden'}>
            <Modal.Section padding="0" height="100%">
                <Group
                    direction="horizontal"
                    height={`calc(100% - ${footerHeight}px)`}
                    overflow={'hidden'}
                >
                    <Group.Item
                        width={`${sideNavigationWidth}px`}
                        direction="vertical"
                        gap="md"
                        height="100%"
                        borderRight="medium"
                        paddingX="md"
                    >
                        <Group.Item padding="md">
                            <Heading fontSize="sm">
                                <Trans>OTHER FILTERS</Trans>
                            </Heading>
                        </Group.Item>
                        <Group.Item>
                            <List>
                                {Object.values(availableAdvancedFilters).map(
                                    (filter) => {
                                        if (
                                            getFilterResults(filter).length > 0
                                        ) {
                                            return (
                                                <List.Item
                                                    borderRadius="lg"
                                                    marginBottom="2xs"
                                                    className={listItemStyles({
                                                        isSelected:
                                                            selectedFilter ===
                                                            filter,
                                                    })}
                                                    onClick={() =>
                                                        setSelectedFilter(
                                                            filter,
                                                        )
                                                    }
                                                    key={`navBar-${filter}`}
                                                >
                                                    <Group
                                                        gap="xs"
                                                        alignItems={'center'}
                                                        display={'flex'}
                                                    >
                                                        <Group.Item>
                                                            {getFilterIcon(
                                                                filter,
                                                            )}
                                                        </Group.Item>
                                                        <Group.Item>
                                                            {getFilterName(
                                                                filter,
                                                            )}
                                                        </Group.Item>
                                                        <FilterBadge
                                                            filter={filter}
                                                        />
                                                    </Group>
                                                </List.Item>
                                            );
                                        } else {
                                            return null;
                                        }
                                    },
                                )}
                            </List>
                        </Group.Item>
                    </Group.Item>
                    <AdvancedFiltersModalData
                        name={getFilterName(selectedFilter) ?? ''}
                        handleClose={handleClose}
                        selectedFilter={selectedFilter}
                    />
                </Group>
            </Modal.Section>
            <Box
                height={`${footerHeight}px`}
                position="absolute"
                bottom="-2px"
                display="flex"
                justifyContent="center"
                width="100%"
                borderTop="medium"
            >
                <Group
                    width="100%"
                    justifyContent="space-between"
                    alignItems="center"
                    padding="lg"
                >
                    <Group.Item>
                        <Button
                            kind="minimal"
                            variant="ghost"
                            onClick={handleClearCta}
                            isDisabled={!filtersAppliedTotal}
                            isRounded
                        >
                            {filtersAppliedTotal ? (
                                <Trans>{`Clear all (${filtersAppliedTotal.toString()})`}</Trans>
                            ) : (
                                <Trans>Clear all </Trans>
                            )}
                        </Button>
                    </Group.Item>
                    <Group.Item>
                        <Button
                            isRounded
                            onClick={handleClose}
                            variant="primary"
                        >
                            <Trans>Show {resultsCopy}</Trans>
                        </Button>
                    </Group.Item>
                </Group>
            </Box>
        </Modal.Content>
    );
};

export default AdvancedFiltersModalContent;
